/**
 * Settings page
 *
 * FIX 6: Added "Buy Credits" button in Billing section.
 *         Shows current balance, description, and direct purchase flow.
 */
import { useAuth } from '../hooks/useAuth';
import { useCredits, usePurchaseCredits } from '../hooks/useCredits';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';

export function Settings() {
  const { user } = useAuth();
  const { data: credits } = useCredits();
  const purchaseCredits = usePurchaseCredits();
  const [loadingPortal, setLoadingPortal] = useState(false);

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      const res = await api.post<ApiSuccess<{ portalUrl: string }>>('/subscriptions/portal');
      window.location.href = res.data.data.portalUrl;
    } catch {
      alert('Failed to open billing portal. Please try again.');
      setLoadingPortal(false);
    }
  };

  const sections = ['Account', 'Billing', 'Security'] as const;
  type Section = typeof sections[number];
  const { hash } = useLocation();
  const [activeSection, setActiveSection] = useState<Section>(
    hash === '#billing' ? 'Billing' : 'Account'
  );

  useEffect(() => {
    if (hash === '#billing') {
      setActiveSection('Billing');
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar variant="authed" />

      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-8">Settings</h1>

        {/* FIX QA-19 MEDIUM-1: flex-col on mobile prevents horizontal overflow at 375px */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar nav — desktop */}
          <nav className="hidden md:flex flex-col w-44 shrink-0 gap-1" aria-label="Settings sections">
            {sections.map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  activeSection === section
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                {section}
              </button>
            ))}
          </nav>

          {/* Mobile tabs — full-width tab strip, only visible below md breakpoint */}
          <div className="md:hidden w-full mb-6 flex gap-1 flex-wrap overflow-x-auto">
            {sections.map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  activeSection === section
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground border border-border hover:bg-accent'
                }`}
              >
                {section}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeSection === 'Account' && (
              <section className="p-6 border border-border rounded-lg bg-card">
                <h2 className="font-semibold text-foreground mb-4">Account</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="text-foreground">{user?.email}</span>
                  </div>
                  {user?.displayName && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="text-foreground">{user.displayName}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {activeSection === 'Billing' && (
              <section className="p-6 border border-border rounded-lg bg-card">
                <h2 className="font-semibold text-foreground mb-4">Credits &amp; Subscription</h2>

                {credits && (
                  <div className="space-y-3 text-sm mb-6">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Current balance</span>
                      <span className="font-medium text-foreground">{credits.creditBalance} credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lifetime books generated</span>
                      <span className="text-foreground">{credits.lifetimeCreditsUsed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Free chapter previews used</span>
                      <span className="text-foreground">{credits.freeChaptersUsed} / 3</span>
                    </div>
                  </div>
                )}

                {/* Subscription management buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <button
                    onClick={() => void handleManageBilling()}
                    disabled={loadingPortal}
                    className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm disabled:opacity-50"
                  >
                    {loadingPortal ? 'Loading...' : 'Manage Billing'}
                  </button>
                  <Link
                    to="/pricing"
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    Upgrade Plan
                  </Link>
                </div>

                {/* FIX 6: Buy Credits section */}
                <div className="border-t border-border pt-5">
                  <h3 className="text-sm font-semibold text-foreground mb-1">Buy Credits</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    10 credits — $9.99. Use credits to generate chapters and covers beyond your plan limit.
                  </p>
                  <button
                    onClick={() => purchaseCredits.mutate()}
                    disabled={purchaseCredits.isPending}
                    className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {purchaseCredits.isPending ? 'Loading...' : 'Buy Credits ($9.99)'}
                  </button>
                </div>
              </section>
            )}

            {activeSection === 'Security' && (
              <section className="p-6 border border-border rounded-lg bg-card">
                <h2 className="font-semibold text-foreground mb-4">Security</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Authentication</span>
                    <span className="text-foreground">Google / Email</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Password changes and 2FA settings are managed through your Google account or Firebase Auth.
                </p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
